// src/pages/products/SparkPlugsPage.tsx
import React from 'react';
import { Button, Modal, Input, Tag, Switch, Checkbox, message } from 'antd';
import { SparkPlugItem } from '../../types/ProductItems';
import SparkPlugForm from '../../components/forms/SparkPlugForm';
import SparkPlugsTable from '../../components/tables/SparkPlugsTable';
import ResponsiveTable from '../../components/common/ResponsiveTable';
import { useProductManager } from '../../hooks/useProductManager';
import { apiSetProductVisibility } from '../../services/productAPI';
import { STORE_NAMES } from '../../utils/db';

const NEEDS_QTY_KEY = 'sparkplugs_needsQty';
// Строк на странице в окне «Скрытые». Число нужно и таблице, и расчёту
// предела страниц — держим в одном месте, чтобы они не разошлись.
const HIDDEN_PAGE_SIZE = 10;

const SparkPlugsPage: React.FC = () => {
  const [search, setSearch] = React.useState('');
  const [hiddenModalOpen, setHiddenModalOpen] = React.useState(false);
  // Поиск внутри окна «Скрытые» — отдельный от поиска страницы: там текст
  // работает ещё и как сканер штрихкода, здесь это только фильтр по списку.
  const [hiddenSearch, setHiddenSearch] = React.useState('');
  // Страница списка скрытых. Держим её сами, чтобы сбрасывать на первую при
  // наборе: иначе, стоя на третьей странице, после фильтра можно оказаться
  // на пустой — совпадений меньше, чем строк до неё.
  const [hiddenPage, setHiddenPage] = React.useState(1);
  // ids unhidden manually — pinned to top + pale-yellow until a quantity is entered
  const [needsQtyIds, setNeedsQtyIds] = React.useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(NEEDS_QTY_KEY) || '[]'); } catch { return []; }
  });
  // ids just unhidden by scanner — pinned to top (no quantity prompt)
  const [scannedIds, setScannedIds] = React.useState<string[]>([]);
  // закрывать ли модалку «Скрытые» после разкрытия одной свечи (опционально)
  const [closeAfterUnhide, setCloseAfterUnhide] = React.useState<boolean>(
    () => localStorage.getItem('sparkplugs_closeAfterUnhide') !== '0',
  );

  const {
    items: sparkPlugs,
    currentItem,
    setCurrentItem,
    isModalVisible,
    setIsModalVisible,
    addItem: handleAddSparkPlugs,
    deleteItem: handleDelete,
    exportToExcel,
    refresh,
  } = useProductManager<SparkPlugItem>(STORE_NAMES.sparkPlugs);

  const persistNeedsQty = React.useCallback((ids: string[]) => {
    setNeedsQtyIds(ids);
    localStorage.setItem(NEEDS_QTY_KEY, JSON.stringify(ids));
  }, []);

  const visibleItems = React.useMemo(() => sparkPlugs.filter(i => !i.hidden), [sparkPlugs]);
  const hiddenItems  = React.useMemo(() => sparkPlugs.filter(i =>  i.hidden), [sparkPlugs]);

  // Drop the needs-qty mark once a quantity is entered (or the item disappears).
  // NB: do NOT clear on `hidden` here — right after unhide the local list is still
  // stale (item shows as hidden until refresh lands), which would wipe the mark.
  React.useEffect(() => {
    if (needsQtyIds.length === 0) return;
    const stillPending = needsQtyIds.filter(id => {
      const it = sparkPlugs.find(p => String(p.id) === String(id));
      return it && (it.quantity ?? 0) <= 0;
    });
    if (stillPending.length !== needsQtyIds.length) persistNeedsQty(stillPending);
  }, [sparkPlugs, needsQtyIds, persistNeedsQty]);

  const handleEdit = (item: SparkPlugItem) => {
    setCurrentItem(item);
    setIsModalVisible(true);
  };

  // Modal toggle: turning the switch OFF makes a hidden plug visible again.
  const handleUnhide = async (item: SparkPlugItem) => {
    try {
      await apiSetProductVisibility(item.id, false);
      persistNeedsQty([String(item.id), ...needsQtyIds.filter(id => id !== String(item.id))]);
      await refresh();
      setSearch('');               // снять фильтр, чтобы строка точно была видна
      if (closeAfterUnhide) setHiddenModalOpen(false); // опционально закрыть модалку
      message.success(`«${item.model}» возвращена в список — внесите количество`);
    } catch (err) {
      message.error((err as Error).message);
    }
  };

  // Barcode scan (sku / part number / model). If it matches a hidden plug, unhide it
  // immediately and pin it to the top — no quantity prompt.
  const handleScan = async () => {
    const q = search.trim();
    if (!q) return;
    const norm = (v: unknown) => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const nq = norm(q);
    const hit = hiddenItems.find(i =>
      [i.sku, i.partNumber, i.model].some(v => v && norm(v) === nq)
    );
    if (!hit) return; // not a hidden plug — leave the text as a normal search filter
    try {
      await apiSetProductVisibility(hit.id, false);
      setScannedIds(prev => [String(hit.id), ...prev.filter(id => id !== String(hit.id))]);
      await refresh();
      setSearch('');
      message.success(`«${hit.model}» найдена сканером и показана`);
    } catch (err) {
      message.error((err as Error).message);
    }
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visibleItems;
    return visibleItems.filter(item =>
      [item.brand, item.partNumber, item.model, item.original, String(item.price)]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [visibleItems, search]);

  // Те же поля, что показаны в таблице скрытых: модель, номер детали, SKU.
  // Пробелы и дефисы снимаются с обеих сторон — «PT 16 TT» и «pt16tt» должны
  // находить PT16TT, как и в поиске на витрине.
  const hiddenFiltered = React.useMemo(() => {
    const norm = (v: unknown) => String(v ?? '').toLowerCase().replace(/[-\s]/g, '');
    const q = norm(hiddenSearch);
    if (!q) return hiddenItems;
    return hiddenItems.filter(item =>
      [item.model, item.partNumber, item.sku].some(v => v && norm(v).includes(q))
    );
  }, [hiddenItems, hiddenSearch]);

  // Страницу зажимаем в допустимый предел при отрисовке, а не только сбросом
  // при наборе: список скрытых укорачивается ещё и когда свечу раскрывают, и
  // после refresh. Одно место вместо трёх сбросов — и пустая страница
  // невозможна в принципе.
  const hiddenPageCount = Math.max(1, Math.ceil(hiddenFiltered.length / HIDDEN_PAGE_SIZE));
  const hiddenCurrentPage = Math.min(hiddenPage, hiddenPageCount);

  const totalCount = visibleItems.length;
  const shownCount = filtered.length;

  const hiddenColumns = [
    { title: 'Модель', dataIndex: 'model', key: 'model' },
    { title: 'Номер детали', dataIndex: 'partNumber', key: 'partNumber' },
    { title: 'SKU', dataIndex: 'sku', key: 'sku', render: (v: string) => v || '—' },
    {
      title: 'Скрыто',
      key: 'hidden',
      width: 120,
      render: (_: unknown, item: SparkPlugItem) => (
        <Switch
          checked
          checkedChildren="Скрыто"
          unCheckedChildren="Показать"
          onChange={(val) => { if (!val) handleUnhide(item); }}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="products-toolbar sp-toolbar">

        <Button type="primary" onClick={() => { setIsModalVisible(true); setCurrentItem(null); }}>
          Добавить свечу зажигания
        </Button>

        <Button onClick={() => exportToExcel('SparkPlugs_Export')}>
          📁 Экспорт в Excel
        </Button>

        <Button onClick={() => setHiddenModalOpen(true)}>
          Скрытые ({hiddenItems.length})
        </Button>

        <Input
          placeholder="Поиск / сканер…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onPressEnter={handleScan}
          className="sp-search"
          allowClear
        />

        <Tag className="small-tag">Всего: {totalCount}</Tag>
        <Tag color="blue" className="small-tag">Показано: {shownCount}</Tag>

      </div>

      <SparkPlugsTable
        data={filtered}
        onEdit={handleEdit}
        onDelete={handleDelete}
        needsQtyIds={needsQtyIds}
        pinnedIds={scannedIds}
      />

      <Modal
        title={currentItem ? 'Редактировать свечу зажигания' : 'Добавить свечу зажигания'}
        open={isModalVisible}
        onCancel={() => { setIsModalVisible(false); setCurrentItem(null); }}
        footer={null}
        destroyOnHidden
      >
        <SparkPlugForm onAdd={handleAddSparkPlugs} initialValues={currentItem} />
      </Modal>

      <Modal
        title="Скрытые свечи зажигания"
        open={hiddenModalOpen}
        onCancel={() => { setHiddenModalOpen(false); setHiddenSearch(''); setHiddenPage(1); }}
        footer={null}
        width={760}
      >
        <Input
          placeholder="Поиск по модели, номеру детали или SKU…"
          value={hiddenSearch}
          onChange={e => { setHiddenSearch(e.target.value); setHiddenPage(1); }}
          className="sp-hidden-search"
          allowClear
        />
        <Checkbox
          checked={closeAfterUnhide}
          onChange={e => {
            setCloseAfterUnhide(e.target.checked);
            localStorage.setItem('sparkplugs_closeAfterUnhide', e.target.checked ? '1' : '0');
          }}
          className="sp-close-after"
        >
          Закрывать окно после разкрытия свечи
        </Checkbox>
        {/* Та же беда, что и на основных страницах: на телефоне у таблицы
            видно три колонки из шести. Карточки решают и здесь. */}
        <ResponsiveTable
          rowKey="id"
          columns={hiddenColumns}
          dataSource={hiddenFiltered}
          size="small"
          pagination={{
            pageSize: HIDDEN_PAGE_SIZE,
            hideOnSinglePage: true,
            current: hiddenCurrentPage,
            onChange: setHiddenPage,
          }}
        />
      </Modal>
    </div>
  );
};

export default SparkPlugsPage;
